/**
 * Aba Auditoria — histórico de mudanças sensíveis (super_admin only).
 *
 * Mostra ações em ordem cronológica reversa: action, entity, diff_summary,
 * user_email, ip, timestamp.
 */

import { useTenantAuditLogs } from "@/hooks/queries";

import { Card, CardBody, CardHeader } from "@/components/Card";
import { Skeleton } from "@/components/Skeleton";
import { Badge } from "@/components/Badge";
import { formatDate } from "@/lib/format";

const ACTION_LABEL: Record<string, { label: string; tone: "default" | "success" | "warning" | "danger" | "brand" }> = {
  create:     { label: "Criação",     tone: "success" },
  update:     { label: "Alteração",   tone: "warning" },
  delete:     { label: "Exclusão",    tone: "danger" },
  deactivate: { label: "Desativação", tone: "danger" },
  logo_upload:{ label: "Logo",        tone: "brand" },
};

const ENTITY_LABEL: Record<string, string> = {
  tenant_company:     "Empresa",
  tenant_responsavel: "Responsável",
  tenant_licenca:     "Licença",
};

export function AuditTab({ tenantId }: { tenantId: string }) {
  const logs = useTenantAuditLogs(tenantId);

  if (logs.isLoading) return <Skeleton className="h-64 w-full" />;

  const items = logs.data ?? [];

  if (items.length === 0) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Sem alterações registradas ainda.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Histórico de Alterações" subtitle={`${items.length} eventos`} />
      <CardBody className="space-y-2">
        {items.map((log) => {
          const action = ACTION_LABEL[log.action] ?? { label: log.action, tone: "default" as const };
          const entity = ENTITY_LABEL[log.entity_type] ?? log.entity_type;
          return (
            <div
              key={log.id}
              className="flex items-start gap-3 border-b border-surface-100 dark:border-slate-800 py-2 last:border-b-0"
            >
              <Badge tone={action.tone}>{action.label}</Badge>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-800 dark:text-slate-200">
                  <span className="font-medium">{entity}</span>
                  {log.diff_summary && (
                    <span className="text-slate-600 dark:text-slate-400">: {log.diff_summary}</span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-500 dark:text-slate-400">
                  <span>{formatDate(log.created_at)}</span>
                  {log.user_email && <span>por {log.user_email}</span>}
                  {log.ip_address && <span className="font-mono">{log.ip_address}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}
